import type { TechnologyIconName } from './supported-icon-names';

export const ICON_PATTERNS: Array<{
  pattern: RegExp;
  icon: TechnologyIconName;
}> = [
  // Languages & runtimes
  { pattern: /\btypescript\b/, icon: 'typescript' },
  { pattern: /\bpython\b/, icon: 'python' },
  { pattern: /\bjava\b/, icon: 'java' },
  { pattern: /\bcsharp\b/, icon: 'csharp' },
  { pattern: /\bgo(lang)?\b|\bgolang\b/, icon: 'go' },
  { pattern: /\brust\b/, icon: 'rust' },
  { pattern: /\bkotlin\b/, icon: 'kotlin' },
  { pattern: /\bswift\b/, icon: 'swift' },
  { pattern: /\bphp\b/, icon: 'php' },
  { pattern: /\bnode(js)?\b/, icon: 'nodejs' },
  { pattern: /\bdeno\b/, icon: 'deno' },
  { pattern: /\bbun(js)?\b/, icon: 'bunjs' },

  // Frontend frameworks & libraries
  { pattern: /\breact(js)?\b/, icon: 'react' },
  { pattern: /\bnext(\.js|js)?\b/, icon: 'nextjs' },
  { pattern: /\bvue(js)?\b/, icon: 'vuejs' },
  { pattern: /\bangular\b/, icon: 'angular' },
  { pattern: /\bsvelte(js)?\b/, icon: 'sveltejs' },
  { pattern: /\bsolid(js)?\b/, icon: 'solidjs' },
  { pattern: /\bnuxt(js)?\b/, icon: 'nuxtjs' },
  { pattern: /\bremix\b/, icon: 'remix' },

  // Styling/UI
  { pattern: /\btailwind(-?css)?\b/, icon: 'tailwindcss' },
  { pattern: /\bmaterial(-?ui)?\b/, icon: 'materialui' },
  { pattern: /\bchakra(-?ui)?\b/, icon: 'chakraui' },
  { pattern: /\bradix(-?ui)?\b/, icon: 'radixui' },
  { pattern: /\bshadcn(-?ui)?\b/, icon: 'shadcnui' },
  { pattern: /\bbootstrap(5)?\b/, icon: 'bootstrap5' },
  { pattern: /\bbulma\b/, icon: 'bulmaui' },

  // State/data & APIs
  { pattern: /\bredux(js)?\b/, icon: 'redux' },
  { pattern: /\bredux(-?)saga\b/, icon: 'reduxsaga' },
  { pattern: /\breact(-?)query\b/, icon: 'reactquery' },
  { pattern: /\breact(-?)router\b/, icon: 'reactrouter' },
  { pattern: /\bgraphql\b/, icon: 'graphql' },

  // Backend frameworks
  { pattern: /\bexpress(js)?\b/, icon: 'expressjs' },
  { pattern: /\bnest(js)?\b/, icon: 'nestjs' },
  { pattern: /\bdjango\b/, icon: 'django' },
  { pattern: /\bflask\b/, icon: 'flask' },
  { pattern: /\bspring\b/, icon: 'spring' },
  { pattern: /\brails\b/, icon: 'rails' },
  { pattern: /\blaravel\b/, icon: 'laravel' },
  {
    pattern: /\basp(\.net)?\b|\baspnet\b|\baspx\b|\bnet(core)?\b/,
    icon: 'netcore',
  },
  { pattern: /\bphoenix\b|\belixir\b/, icon: 'elixir' },
  { pattern: /\bfastify\b/, icon: 'nodejs' },
  { pattern: /\bkoa\b/, icon: 'nodejs' },

  // Databases & storage
  { pattern: /\bpostgres(ql)?\b|@vercel\/postgres/, icon: 'postgresql' },
  { pattern: /\bmysql\b/, icon: 'mysql' },
  { pattern: /\bmaria(db)?\b/, icon: 'mariadb' },
  { pattern: /\bmongo(db)?\b/, icon: 'mongodb' },
  { pattern: /\bredis\b/, icon: 'redis' },
  { pattern: /\boracle\b/, icon: 'oracle' },
  { pattern: /\belastic(search)?\b/, icon: 'elastic' },
  { pattern: /\bcassandra\b/, icon: 'cassandradb' },
  { pattern: /\bclickhouse\b/, icon: 'clickhouse' },
  { pattern: /\bsupabase\b/, icon: 'supabase' },
  { pattern: /\bprisma\b/, icon: 'prisma' },

  // Auth & security
  { pattern: /\bauth0\b/, icon: 'auth0' },

  // Tooling & build
  { pattern: /\bwebpack\b/, icon: 'webpack' },
  { pattern: /\bvite(js)?\b/, icon: 'vitejs' },
  { pattern: /\brollup\b/, icon: 'rollup' },
  { pattern: /\besbuild\b/, icon: 'esbuild' },
  { pattern: /\bbabel\b/, icon: 'babel' },
  { pattern: /\beslint\b/, icon: 'eslint' },
  { pattern: /\bprettier\b/, icon: 'prettier' },
  { pattern: /\bstorybook\b/, icon: 'storybook' },
  { pattern: /\bjest\b/, icon: 'jest' },
  { pattern: /\bvitest\b/, icon: 'vitest' },
  { pattern: /\bcypress\b/, icon: 'cypress' },
  { pattern: /\bplaywright\b/, icon: 'playwright' },
  { pattern: /\bgulp\b/, icon: 'gulp' },
  { pattern: /\bgrunt\b/, icon: 'gruntjs' },

  // DevOps / Infra / Cloud / Edge
  { pattern: /\bdocker\b/, icon: 'docker' },
  { pattern: /\bkubernetes|k8s\b/, icon: 'kubernetes' },
  { pattern: /\bapache\b/, icon: 'apache' },
  { pattern: /\bbedrock\b|\bamazon\s?bedrock\b/i, icon: 'bedrock' },
  { pattern: /\baws\b|^amazon|^awselb|^awsalb|^cloudfront/i, icon: 'aws' },
  { pattern: /\bazure\b/, icon: 'azure' },
  {
    pattern:
      /\bgcp\b|\bgoogle\s?cloud\b|\bgcloud\b|\bgoogle\s?frontend\b|\bgfe\b/i,
    icon: 'gcloud',
  },
  { pattern: /\bvercel\b/, icon: 'vercel' },
  { pattern: /\bnetlify\b/, icon: 'netlify' },
  { pattern: /\bcloudflare\b/, icon: 'cloudflare' },

  // VCS / CI / Ticketing
  { pattern: /\bgit(hub)?\b/, icon: 'github' },
  { pattern: /\bgitlab\b/, icon: 'gitlab' },
  { pattern: /\bcircle(ci)?\b/, icon: 'circleci' },
  { pattern: /\bjira\b/, icon: 'jira' },
  { pattern: /\blinear\b/, icon: 'linear' },
];
